import { Injectable, signal } from '@angular/core'
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker'
import { filter } from 'rxjs/operators'

/**
 * Owns the PWA update lifecycle.
 *
 * The old behaviour silently force-reloaded the moment a new bundle was ready
 * and only ever checked once, at registration. That had two failure modes:
 *  - a reload could yank the page out from under a dispatcher mid-action, and
 *  - a long-lived session (a dispatcher who never closes the PWA) never got a
 *    second update check, so new code never arrived — which is why some users
 *    had to reinstall the PWA to pick up a release.
 *
 * Instead we surface a non-blocking "update ready" prompt (see
 * UpdateBannerComponent) and let the user reload when it's safe, and we keep
 * polling for new bundles on a timer and whenever the app regains focus.
 */
@Injectable({ providedIn: 'root' })
export class UpdateService {
  /** True once a new version has downloaded and is ready to activate. */
  readonly updateReady = signal(false)

  private started = false

  // Re-check for a new bundle every 30 minutes while the app stays open.
  private static readonly POLL_MS = 30 * 60 * 1000

  constructor(private swUpdate: SwUpdate) {}

  /** Wire up update detection. Idempotent — safe to call more than once. */
  init(): void {
    if (this.started || !this.swUpdate.isEnabled) return
    this.started = true

    // A new version finished downloading → offer the reload.
    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this.updateReady.set(true))

    // NGSW decided the current state is unrecoverable (e.g. a cached asset is
    // missing and can't be re-fetched). The only fix is a hard reload onto a
    // fresh worker — there's nothing for the user to decide here.
    this.swUpdate.unrecoverable.subscribe(() => location.reload())

    // Initial check, then poll for long-open sessions.
    this.check()
    setInterval(() => this.check(), UpdateService.POLL_MS)

    // Also check the instant the app returns to the foreground — a dispatcher
    // reopening the PWA after hours is exactly when a new build is most likely
    // waiting on the server.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.check()
    })
  }

  /** Ask NGSW to look for a new bundle now. Failures are non-fatal. */
  check(): void {
    this.swUpdate.checkForUpdate().catch(() => undefined)
  }

  /** Activate the downloaded version and reload onto it. */
  async applyUpdate(): Promise<void> {
    try {
      await this.swUpdate.activateUpdate()
    } catch (err) {
      console.error('[UpdateService] activate failed', err)
    } finally {
      location.reload()
    }
  }
}
